/**
 * Sprint G — Legal templates embedded as JS strings so they render without
 * needing markdown loaders. Source of truth (with lawyer-hand-off notes)
 * lives at /app/memory/legal_stubs.md — keep these in sync.
 */

export const TERMS_MD = `
## 1. Acceptance of Terms
By signing up for or using Nexus One ("Service", "we", "us"), you agree to these Terms of Service. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization.

## 2. Description of Service
Nexus One is a multi-tenant enterprise SaaS platform that lets companies hire and interact with AI-powered virtual employees ("AI Workers") backed by third-party large language models. Features include workspace collaboration, integrations, meetings transcription, and usage-tracked billing.

## 3. Subscription and Payment
- Subscriptions are billed in advance on a monthly or annual basis via Stripe.
- Fees are non-refundable except where required by law or explicitly offered ("Founding Customer refund pledge" — see §14).
- Prices may change with 30 days' notice; existing subscription terms are honored for the current billing period.
- Founding-customer pricing (50% off list, locked in) is honored for the lifetime of the subscription so long as the account remains in good standing.

## 4. Cancellation
You may cancel any subscription at any time from your billing portal. Cancellation stops future renewals; you retain access through the end of the current paid period.

## 5. Intellectual Property
- Nexus One retains all rights, title, and interest in the Service, including its AI persona designs, prompts, and platform code.
- Content you submit (documents, chat messages, meeting transcripts, knowledge uploads) remains yours. You grant us a limited license to process it solely to deliver the Service.
- AI outputs generated on your behalf are yours to use, but are provided "as generated" — you are responsible for reviewing them before external use.

## 6. Acceptable Use
You agree not to:
- Reverse engineer, resell, or sublicense the Service.
- Use the Service to generate content that is illegal, defamatory, infringing, or that violates a third party's rights.
- Attempt to bypass tenant isolation, rate limits, or access controls.
- Submit personal data of individuals from whom you don't have appropriate consent.

## 7. Data Security
We maintain administrative, technical, and physical safeguards designed to protect customer data — see our Privacy Policy and DPA.

## 8. Warranty Disclaimers
The Service is provided "as-is" and "as-available" without warranty of any kind, express or implied, including merchantability, fitness for a particular purpose, non-infringement, or accuracy of AI-generated output. AI outputs are not legal, financial, medical, or professional advice.

## 9. Limitation of Liability
To the maximum extent permitted by law, our aggregate liability for any claim shall not exceed the greater of (a) $100 or (b) the fees you paid us in the twelve (12) months preceding the claim.

## 10. Indemnification
You will indemnify us against any third-party claims arising from your misuse of the Service or your content.

## 11. Termination
We may suspend or terminate accounts that violate these Terms or that put the Service or other customers at risk. On termination you may export your data within 30 days after which it may be deleted.

## 12. Governing Law
These Terms are governed by the laws of the state of {{state_placeholder}}, USA, without regard to conflict-of-law principles. Disputes will be resolved in the courts located in {{county_placeholder}}, {{state_placeholder}}.

## 13. Changes to these Terms
We may update these Terms with reasonable notice via email or in-product banner. Continued use after the effective date constitutes acceptance.

## 14. Founding-Customer Refund Pledge
If, during your first 12 months as a founding customer, we materially fail to deliver on the roadmap commitments made in your offer letter, we will refund your subscription pro rata upon written request within 30 days of notice.

## 15. Contact
Questions? Contact us at legal@nexus-one.com (placeholder — replace with real address after review).
`;

export const PRIVACY_MD = `
## 1. What we collect
- **Account data**: name, email, company name, role, hashed password.
- **Content you submit**: documents, chat messages with AI workers, uploaded knowledge, meeting audio and transcripts, connector data brought in from third-party integrations.
- **Usage data**: page views, feature usage, LLM token counts, IP address, device metadata.
- **Payment data**: handled by Stripe. We store only a Stripe customer reference — never your card details.

## 2. How we use it
- Deliver, secure, and improve the Service.
- Route your content to AI providers (see §3) to generate outputs on your behalf.
- Send transactional emails (billing, account) and, with your consent, product updates.
- Comply with legal obligations.

## 3. Subprocessors (illustrative)

| Subprocessor         | Purpose                                                             |
| -------------------- | ------------------------------------------------------------------- |
| Anthropic (Claude)   | LLM inference for AI worker chat and briefings                      |
| OpenAI (Whisper)     | Audio transcription for meetings                                    |
| MongoDB Atlas        | Primary application database                                        |
| Stripe               | Subscription billing and payment processing                         |
| Resend               | Transactional email (demo requests, notifications)                  |
| Sentry               | Error monitoring (PII scrubbed via **beforeSend**)                  |
| AWS / Emergent hosts | Compute, storage, and preview infrastructure                        |

Current live subprocessor list is available on request via legal@nexus-one.com.

## 4. Retention and deletion
- Account and content data are retained while your subscription is active.
- On termination you have 30 days to export data; after that it may be deleted.
- Chat and transcript data can be deleted from within the app at any time.
- Backups are rotated on a 90-day schedule.

## 5. Your rights (GDPR / CCPA placeholder)
Depending on where you live, you may have rights to:
- Access or export the personal data we hold about you.
- Correct inaccuracies.
- Request deletion of personal data (subject to legal retention needs).
- Object to certain processing.

Requests: legal@nexus-one.com. We respond within 30 days.

## 6. International transfers
Customer data may be processed in the United States and in the regions where our subprocessors operate. We rely on standard contractual clauses and other legal mechanisms for cross-border transfers.

## 7. Cookies
We use functional cookies (auth session, preferences) and no third-party tracking cookies by default. If we add analytics in future you will see a cookie banner.

## 8. Security
Data is encrypted in transit (TLS) and at rest where the provider supports it. Access to production systems is restricted and audited.

## 9. Contact
legal@nexus-one.com (placeholder — replace with real address after review).
`;

export const DPA_MD = `
## 1. Subject matter and duration
This DPA governs Nexus One's processing of Customer Personal Data on behalf of the Customer under the Terms of Service, for the duration of the Service subscription.

## 2. Nature and purpose of processing
Processing is limited to what's necessary to deliver the Service: storing content, routing prompts to AI providers, generating outputs, sending notifications, and providing usage analytics.

## 3. Categories of data
- Identifiers (name, email, employee id).
- Business content submitted to the Service (documents, chat, transcripts, knowledge).
- Usage and telemetry data.

## 4. Categories of data subjects
- Customer's employees, contractors, and agents.
- Third-party contacts included in Customer content (e.g., counterparties named in contracts uploaded to Ivan).

## 5. Subprocessors
Customer authorizes the use of the subprocessors listed in our Privacy Policy §3. We will provide 30 days' notice before adding or replacing subprocessors; Customer may object in writing and, if the objection cannot be resolved, terminate the Service without penalty.

## 6. Security measures
- Encryption in transit (TLS 1.2+).
- Encryption at rest where the underlying platform supports it.
- Role-based access controls; least-privilege engineering access.
- Tenant isolation enforced at the application layer.
- Logging and monitoring; annual review of security posture.
- Incident response plan; Customer notified within 72 hours of a confirmed breach affecting their data.

## 7. Data return and deletion
On termination, Customer may export all their Personal Data within 30 days. After that, Nexus One will delete Personal Data within 90 days, excluding backups which age out on the rotation schedule (§4 Privacy).

## 8. Audit
Customer may request annual audit rights via legal@nexus-one.com. Reasonable audits will be scheduled during business hours with minimum disruption.

## 9. Sub-processor changes
Notification via email to the Customer's billing contact and a banner in the Owner Console at least 30 days prior to change.

## 10. Order of precedence
Where the DPA conflicts with the ToS, the DPA controls with respect to processing of Personal Data.

## 11. Governing law
As set forth in the Terms of Service.
`;
